import { get, post } from '../api.js';
import { hasKeys, wrapKeyWithEncKey, base64urlDecode } from '../crypto.js';
import { navigate, currentParams } from '../router.js';
import { esc } from '../app.js';
import { humanSize, startCountdown, downloadAndDecryptDrop } from '../utils.js';
import { getToken, setToken, getDropClaimId, getDropClaimKey, setDropClaim, clearDropClaim, clearDropAutoClaim, setDropAutoClaim, setPostAuthRedirect } from '../auth-store.js';

export async function renderDropClaim(app, params) {
  const dropId = params.id;
  const { params: qp } = currentParams();
  let keyStr = qp.get('key');
  // Strip key from URL to prevent history/referrer leakage
  if (keyStr) {
    history.replaceState(null, '', `#/drop/${dropId}/claim`);
  }
  const magicToken = qp.get('token');

  // Fall back to persisted key if URL was already stripped (refresh, new tab)
  if (!keyStr && dropId) {
    const storedId = getDropClaimId();
    if (storedId === dropId) {
      keyStr = getDropClaimKey();
    }
  }

  if (!dropId || !keyStr) {
    app.innerHTML = '<div class="msg msg-error">Invalid claim link</div>';
    return;
  }

  // Persist across auth redirects and new tabs
  setDropClaim(dropId, keyStr);

  // Auto-authenticate if magic token is present (from email share link)
  if (magicToken && !getToken()) {
    app.innerHTML = '<p class="loading">Signing in...</p>';
    try {
      const result = await post('/auth/magic-link/verify', { token: magicToken });
      setToken(result.token);
      // Continue to drop info page below
    } catch (err) {
      app.innerHTML = `<div class="msg msg-error">Sign-in failed: ${esc(err.message)}</div>
        <div class="actions" style="margin-top:12px"><a href="#/drop/${esc(dropId)}?key=${encodeURIComponent(keyStr)}" class="btn btn-secondary">Download instead</a></div>`;
      return;
    }
  }

  const token = getToken();

  if (!token) {
    renderSignInPrompt(app, dropId, keyStr);
    return;
  }

  // If authenticated and vault unlocked, auto-claim immediately.
  // The user is at /drop/:id/claim specifically to claim.
  if (hasKeys()) {
    clearDropAutoClaim();
    await processClaim(app, dropId, keyStr);
    return;
  }

  // Show drop info page with download + claim options (vault still locked)
  await renderDropInfo(app, dropId, keyStr);
}

async function renderDropInfo(app, dropId, keyStr) {
  app.innerHTML = '<p class="loading">Loading drop...</p>';

  try {
    const drop = await get(`/drops/${dropId}`);

    if (drop.claimed) {
      app.innerHTML = `
        <div class="card" style="text-align:center">
          <h2>Already Claimed</h2>
          <p style="color:var(--text-dim)">This drop has been claimed and is no longer available.</p>
          <div class="actions" style="justify-content:center;margin-top:1rem">
            <a href="#/items" class="btn btn-primary">Go to Items</a>
          </div>
        </div>`;
      return;
    }

    const expiresAt = new Date(drop.expires_at);
    const sizeStr = humanSize(drop.size_bytes);
    const vaultUnlocked = hasKeys();

    app.innerHTML = `
      <div class="card" style="text-align:center">
        <h2>Encrypted Drop</h2>
        <p style="color:var(--text-dim);margin-bottom:1rem"><span id="drop-filename"></span> ${sizeStr}</p>
        <div class="countdown" id="countdown"></div>
        <div id="dl-msg" style="min-height:1.5em"></div>
        <div class="actions" style="justify-content:center;margin-top:1rem;flex-wrap:wrap">
          <button class="btn btn-primary" id="download-btn">Download & Decrypt</button>
          <button class="btn btn-secondary" id="claim-btn">Claim to Vault</button>
        </div>
      </div>`;

    startCountdown(expiresAt);

    // Download & Decrypt button
    document.getElementById('download-btn').onclick = async () => {
      const btn = document.getElementById('download-btn');
      const msg = document.getElementById('dl-msg');
      btn.disabled = true;

      try {
        const filename = await downloadAndDecryptDrop(dropId, keyStr, msg);
        const fnEl = document.getElementById('drop-filename');
        if (fnEl) fnEl.textContent = filename;

        msg.innerHTML = '<span style="color:var(--success)">Downloaded!</span>';
        btn.disabled = false;
      } catch (err) {
        msg.innerHTML = `<span style="color:var(--danger)">${esc(err.message)}</span>`;
        btn.disabled = false;
      }
    };

    // Claim to Vault button
    document.getElementById('claim-btn').onclick = async () => {
      if (hasKeys()) {
        await processClaim(app, dropId, keyStr);
      } else {
        const msg = document.getElementById('dl-msg');
        msg.innerHTML = `
          <p style="color:var(--text-dim);margin-bottom:0.5rem">Unlock your vault first to claim this drop.</p>
          <button class="btn btn-primary" id="unlock-btn">Unlock Vault</button>`;
        document.getElementById('unlock-btn').onclick = async () => {
          setDropAutoClaim();
          setPostAuthRedirect(`#/drop/${dropId}/claim?key=${encodeURIComponent(keyStr)}`);
          try {
            const me = await get('/auth/me');
            const needsSetup = me.public_key.length <= 1;
            navigate(needsSetup ? '#/setup-password' : '#/unlock');
          } catch {
            navigate('#/unlock');
          }
        };
      }
    };
  } catch (err) {
    if (err.message.includes('404') || err.message.includes('Not Found')) {
      app.innerHTML = `
        <div class="card" style="text-align:center">
          <h2>Drop Not Found</h2>
          <p style="color:var(--text-dim)">This drop has expired or does not exist.</p>
        </div>`;
    } else {
      app.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
    }
  }
}

function renderSignInPrompt(app, dropId, keyStr) {
  const redirect = `#/drop/${dropId}/claim?key=${encodeURIComponent(keyStr)}`;
  setPostAuthRedirect(redirect);

  app.innerHTML = `
    <div class="card">
      <h1>Claim This Drop</h1>
      <p style="color:var(--text-dim);margin-bottom:1rem">
        Sign in to save this file permanently to your BlindKeep vault. The drop will be re-encrypted with your keys.
      </p>
      <div id="claim-msg"></div>
      <form id="claim-email-form">
        <label>Email <input type="email" name="email" required></label>
        <button type="submit" class="btn btn-primary">Send Magic Link</button>
      </form>
      <p style="margin-top:1rem;font-size:0.85rem;color:var(--text-dim)">
        Don't have an account? <a href="#/register">Register</a>
      </p>
    </div>
  `;

  document.getElementById('claim-email-form').onsubmit = async (e) => {
    e.preventDefault();
    const msg = document.getElementById('claim-msg');
    const btn = e.target.querySelector('button');
    const email = e.target.email.value;
    btn.disabled = true;
    msg.innerHTML = '';

    try {
      await post('/auth/magic-link', { email });
      msg.innerHTML = `<div class="msg msg-success">Check your email for a magic link. After signing in, you'll be redirected back here.</div>`;
      btn.style.display = 'none';
      e.target.querySelector('input').disabled = true;
    } catch (err) {
      msg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
      btn.disabled = false;
    }
  };
}

async function processClaim(app, dropId, keyStr) {
  app.innerHTML = '<p class="loading">Claiming drop...</p>';

  try {
    const rawKey = base64urlDecode(keyStr);

    // Verify drop is still available
    const drop = await get(`/drops/${dropId}`);
    if (drop.claimed) throw new Error('This drop has already been claimed.');

    // Wrap the ephemeral drop key with user's encKey
    // The backend copies the S3 blob as-is, so no re-upload needed
    const wrapped = wrapKeyWithEncKey(rawKey);

    await post(`/drops/${dropId}/claim`, {
      wrapped_key: wrapped.wrapped_key,
      nonce: wrapped.nonce,
    });

    // Clean up
    clearDropClaim();

    app.innerHTML = `
      <div class="card" style="text-align:center">
        <h2>Drop Claimed</h2>
        <p style="color:var(--text-dim);margin-bottom:1rem">The file has been saved to your BlindKeep vault.</p>
        <div class="actions" style="justify-content:center">
          <a href="#/items" class="btn btn-primary">Go to Items</a>
        </div>
      </div>`;
  } catch (err) {
    app.innerHTML = `
      <div class="msg msg-error">${esc(err.message)}</div>
      <div class="actions"><a href="#/drop/${esc(dropId)}?key=${encodeURIComponent(keyStr)}" class="btn btn-secondary">Back to Drop</a></div>`;
  }
}
